import { useState, useRef, useEffect } from 'react';
import { IconChevronDown } from '@tabler/icons-react';

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
}

export function ExpandableText({ text, maxLines = 1, className = '' }: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      const hasOverflow = el.scrollHeight > el.clientHeight + 1; // +1 as a safe margin
      setIsOverflowing(hasOverflow);
    }
  }, [text, maxLines]);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className={`flex items-start gap-2 ${className}`}>
      <div
        className="flex-1 transition-all duration-300 ease-in-out"
        style={{
          maxHeight: !isExpanded ? `${maxLines * 1.5}rem` : '200px',
          overflow: isExpanded ? 'auto' : 'hidden',
        }}
      >
        <span
          ref={textRef}
          className="block"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: !isExpanded ? maxLines : 'none',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.5rem',
          }}
        >
          {text}
        </span>
      </div>

      {isOverflowing && (
        <button
          onClick={toggleExpanded}
          className="self-start text-muted-foreground hover:text-foreground transition-all duration-300 flex-shrink-0 mt-0.5 transform hover:scale-110"
          type="button"
          aria-label={isExpanded ? 'Collapse text' : 'Expand text'}
        >
          <div
            className="transition-transform duration-300 ease-in-out"
            style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <IconChevronDown size={16} />
          </div>
        </button>
      )}
    </div>
  );
}
